/**
 * Cart Drawer Upsell Component
 * Displays recommended products in the cart drawer with quick add functionality.
 */

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.productsContainer = null;
    this.cartProductIds = [];
    this.cartVariantIds = [];
    this.maxProducts = 4;
    this.useCollection = false;
    this.collectionHandle = '';
    this.productId = '';
  }

  connectedCallback() {
    // Initialize properties from data attributes
    this.productsContainer = this.querySelector('[ref="productsContainer"]');
    this.cartProductIds = this.getCartProductIds();
    this.cartVariantIds = this.getCartVariantIds();
    this.maxProducts = parseInt(this.dataset.maxProducts) || 4;
    this.useCollection = this.dataset.useCollection === 'true';
    this.collectionHandle = this.dataset.collectionHandle || '';
    this.productId = this.dataset.productId || '';

    // Verify container exists
    if (!this.productsContainer) {
      this.hidden = true;
      return;
    }

    // Use requestAnimationFrame to ensure DOM and styles are ready
    // This is important when element is added via morphSection
    requestAnimationFrame(() => {
      this.checkAndInitialize();
    });
  }

  /**
   * Check visibility and initialize or wait
   */
  checkAndInitialize() {
    // Skip if already initialized
    if (this._initialized) return;

    // Check if component is hidden (e.g., mobile version on desktop or vice versa)
    if (this.isHidden()) {
      this.waitForVisibility();
      return;
    }

    this.initialize();
  }

  /**
   * Check if this element or any parent is hidden via CSS display:none
   */
  isHidden() {
    // Check own display
    const style = window.getComputedStyle(this);
    if (style.display === 'none') return true;

    // Check parent container (for mobile upsell wrapped in .cart-drawer__upsell-mobile)
    const parent = this.parentElement;
    if (parent) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'none') return true;
    }

    return false;
  }

  /**
   * Wait for dialog to open and check if this component becomes visible
   */
  waitForVisibility() {
    const dialog = this.closest('dialog');

    if (!dialog) {
      this.initialize();
      return;
    }

    // If dialog is already open and we're visible, initialize immediately
    if (dialog.hasAttribute('open') && !this.isHidden()) {
      this.initialize();
      return;
    }

    // Otherwise wait for dialog to open
    const observer = new MutationObserver(() => {
      if (dialog.hasAttribute('open') && !this.isHidden()) {
        observer.disconnect();
        this.initialize();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Initialize the component - load products and setup handlers
   */
  initialize() {
    // Skip if already initialized
    if (this._initialized) return;
    this._initialized = true;

    this.setupEventListeners();

    // Load complementary products if not using a manual collection
    if (!this.useCollection) {
      this.loadComplementaryProducts();
    } else {
      // Setup click handlers for pre-rendered products
      this.setupProductHandlers();
      this.initDots();
    }
  }

  getCartProductIds() {
    const ids = this.dataset.cartProductIds || '';
    return ids.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));
  }

  getCartVariantIds() {
    const ids = this.dataset.cartVariantIds || '';
    return ids.split(',').filter(id => id.trim()).map(id => parseInt(id.trim()));
  }

  setupEventListeners() {
    // Listen for cart changes to update the upsell section
    document.addEventListener('cart:change', this.handleCartChange.bind(this));

    // Also listen for the theme's cart update event if it exists
    document.addEventListener('cart:refresh', this.handleCartChange.bind(this));

    // Use event delegation for product interactions - this survives DOM updates
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  /**
   * Delegated click handler - handles all clicks within the upsell component
   */
  handleClick(event) {
    // Handle show variants button
    const showVariantsButton = event.target.closest('[data-action="show-variants"]');
    if (showVariantsButton) {
      event.preventDefault();
      this.toggleVariantDropdown(showVariantsButton);
      return;
    }

    // Handle add to cart (including variant options)
    const addButton = event.target.closest('[data-action="add-to-cart"]');
    if (addButton) {
      event.preventDefault();
      this.handleAddToCart(addButton);
      return;
    }
  }

  /**
   * Toggle variant dropdown visibility
   */
  toggleVariantDropdown(button) {
    const wrapper = button.closest('.cart-drawer__upsell-variant-wrapper');
    if (!wrapper) return;

    const dropdown = wrapper.querySelector('.cart-drawer__upsell-variant-dropdown');
    if (!dropdown) return;

    const isOpen = wrapper.classList.contains('is-open');

    // Close all other dropdowns first
    this.closeAllDropdowns();

    if (!isOpen) {
      wrapper.classList.add('is-open');
      dropdown.hidden = false;

      // Auto-scroll to make dropdown visible (desktop only)
      requestAnimationFrame(() => {
        this.scrollDropdownIntoView(dropdown);
      });

      // Add click outside listener
      setTimeout(() => {
        document.addEventListener('click', this.handleClickOutside);
      }, 0);
    }
  }

  /**
   * Scroll the upsell container to make the dropdown fully visible
   */
  scrollDropdownIntoView(dropdown) {
    // Find the scrollable upsell container (this element itself)
    const scrollContainer = this;
    if (!scrollContainer || scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
      return; // Not scrollable or doesn't need scrolling
    }

    const dropdownRect = dropdown.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    // Check if dropdown bottom is below container bottom
    const dropdownBottom = dropdownRect.bottom;
    const containerBottom = containerRect.bottom;

    if (dropdownBottom > containerBottom) {
      // Calculate how much we need to scroll
      const scrollAmount = dropdownBottom - containerBottom + 16; // 16px padding
      scrollContainer.scrollBy({
        top: scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Close all variant dropdowns
   */
  closeAllDropdowns() {
    this.querySelectorAll('.cart-drawer__upsell-variant-wrapper.is-open').forEach(wrapper => {
      wrapper.classList.remove('is-open');
      const dropdown = wrapper.querySelector('.cart-drawer__upsell-variant-dropdown');
      if (dropdown) dropdown.hidden = true;
    });
    document.removeEventListener('click', this.handleClickOutside);
  }

  /**
   * Handle click outside to close dropdown
   */
  handleClickOutside = (event) => {
    const wrapper = event.target.closest('.cart-drawer__upsell-variant-wrapper');
    if (!wrapper) {
      this.closeAllDropdowns();
    }
  }

  /**
   * Delegated change handler - handles variant selector changes
   */
  handleChange(event) {
    // Handle native select (mobile) - add to cart directly
    const nativeSelect = event.target.closest('[data-action="native-variant-select"]');
    if (nativeSelect && nativeSelect.value) {
      this.handleNativeSelectAddToCart(nativeSelect);
      return;
    }

    // Legacy support for old variant select
    const select = event.target.closest('[data-action="select-variant"]');
    if (select) {
      this.handleVariantSelect(select);
    }
  }

  /**
   * Handle add to cart from native select (mobile)
   */
  async handleNativeSelectAddToCart(select) {
    const variantId = select.value;
    if (!variantId) return;

    const productCard = select.closest('.cart-drawer__upsell-product');
    if (!productCard) return;

    // Set loading state
    select.disabled = true;

    try {
      const sectionIds = this.getCartSectionIds();

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const result = await response.json();

      // Dispatch cart:update event
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: 'cart-drawer-upsell',
          data: {
            itemCount: result.item_count,
            source: 'cart-drawer-upsell',
            variantId: variantId,
            sections: result.sections || {}
          }
        }
      }));

      // Animate removal of product card
      productCard.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      productCard.style.opacity = '0';
      productCard.style.transform = 'scale(0.9)';
      setTimeout(() => {
        productCard.remove();
        if (this.querySelectorAll('.cart-drawer__upsell-product').length === 0) {
          this.hidden = true;
        }
      }, 300);

    } catch (error) {
      console.error('Error adding to cart:', error);
      select.disabled = false;
      select.selectedIndex = 0; // Reset to placeholder
    }
  }

  setupProductHandlers() {
    // No longer needed - using event delegation instead
    // Kept for backwards compatibility with dynamically rendered products
  }

  async loadComplementaryProducts() {
    if (!this.productId) {
      this.showNoProducts();
      return;
    }

    // Verify Shopify routes exist
    if (!window.Shopify?.routes?.root) {
      console.warn('Cart drawer upsell: Shopify routes not available');
      this.showNoProducts();
      return;
    }

    try {
      // Fetch product recommendations using Shopify's Product Recommendations API
      const response = await fetch(
        `${window.Shopify.routes.root}recommendations/products.json?product_id=${this.productId}&limit=${this.maxProducts + this.cartProductIds.length}&intent=complementary`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }

      const data = await response.json();
      let products = data.products || [];

      // Filter out products already in cart
      products = products.filter(product => !this.cartProductIds.includes(product.id));

      // Limit to max products
      products = products.slice(0, this.maxProducts);

      if (products.length === 0) {
        // Try related products if no complementary products found
        await this.loadRelatedProducts();
        return;
      }

      this.renderProducts(products);
    } catch (error) {
      console.error('Error loading complementary products:', error);
      // Try related products as fallback
      await this.loadRelatedProducts();
    }
  }

  async loadRelatedProducts() {
    if (!this.productId) {
      this.showNoProducts();
      return;
    }

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}recommendations/products.json?product_id=${this.productId}&limit=${this.maxProducts + this.cartProductIds.length}&intent=related`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch related products: ${response.status}`);
      }

      const data = await response.json();
      let products = data.products || [];

      // Filter out products already in cart
      products = products.filter(product => !this.cartProductIds.includes(product.id));

      // Limit to max products
      products = products.slice(0, this.maxProducts);

      if (products.length === 0) {
        this.showNoProducts();
        return;
      }

      this.renderProducts(products);
    } catch (error) {
      console.error('Error loading related products:', error);
      this.showNoProducts();
    }
  }

  showNoProducts() {
    // Hide the entire upsell section when no products available
    this.hidden = true;
    // Also clear the loading state
    if (this.productsContainer) {
      this.productsContainer.innerHTML = '';
    }
  }

  renderProducts(products) {
    if (!this.productsContainer) return;

    this.productsContainer.innerHTML = products.map(product => this.renderProductCard(product)).join('');
    this.setupProductHandlers();
    this.initDots();
  }

  /* Mobile slide dots (pagination) for the horizontal upsell strip */
  initDots() {
    if (!this.classList.contains('cart-drawer__upsell--mobile')) return;
    const dots = this.querySelector('[ref="dots"]');
    const scroller = this.productsContainer;
    if (!dots || !scroller) return;
    if (!this._dotsBound) {
      scroller.addEventListener('scroll', () => this.updateDots(), { passive: true });
      window.addEventListener('resize', () => this.buildDots(), { passive: true });
      // The drawer animates open, so the strip only gets its real width after paint —
      // rebuild whenever the scroller's size changes (and once images load).
      if (window.ResizeObserver) {
        this._dotsRO = new ResizeObserver(() => this.buildDots());
        this._dotsRO.observe(scroller);
      }
      scroller.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.buildDots(), { once: true });
      });
      this._dotsBound = true;
    }
    this.buildDots();
    setTimeout(() => this.buildDots(), 150);
    setTimeout(() => this.buildDots(), 500);
  }

  buildDots() {
    const dots = this.querySelector('[ref="dots"]');
    const scroller = this.productsContainer;
    if (!dots || !scroller) return;
    const cw = scroller.clientWidth;
    const pages = cw ? Math.max(1, Math.ceil((scroller.scrollWidth - 2) / cw)) : 1;
    if (pages < 2) { dots.innerHTML = ''; return; }
    if (dots.childElementCount !== pages) {
      dots.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'cart-drawer__upsell-dot';
        d.setAttribute('aria-label', 'Slide ' + (i + 1));
        ((idx) => { d.addEventListener('click', () => scroller.scrollTo({ left: idx * scroller.clientWidth, behavior: 'smooth' })); })(i);
        dots.appendChild(d);
      }
    }
    this.updateDots();
  }

  updateDots() {
    const dots = this.querySelector('[ref="dots"]');
    const scroller = this.productsContainer;
    if (!dots || !scroller || !dots.children.length) return;
    const cw = scroller.clientWidth || 1;
    const idx = Math.round(scroller.scrollLeft / cw);
    for (let i = 0; i < dots.children.length; i++) {
      dots.children[i].classList.toggle('is-active', i === idx);
    }
  }

  renderProductCard(product) {
    // Count available variants
    const availableVariants = product.variants.filter(v => v.available);
    const showVariantSelector = availableVariants.length > 1;

    const firstAvailableVariant = availableVariants[0] || product.variants[0];
    const hasComparePrice = product.compare_at_price && product.compare_at_price > product.price;

    const imageUrl = product.featured_image
      ? this.getSizedImageUrl(product.featured_image, '400x400')
      : '';

    const priceHtml = hasComparePrice
      ? `<span class="cart-drawer__upsell-product-price--sale">${this.formatMoney(product.price)}</span>
         <span class="cart-drawer__upsell-product-price--compare">${this.formatMoney(product.compare_at_price)}</span>`
      : this.formatMoney(product.price);

    const vendorHtml = product.vendor
      ? `<div class="cart-drawer__upsell-product-vendor">${this.escapeHtml(product.vendor)}</div>`
      : '';

    let actionHtml;

    if (showVariantSelector) {
      // Multiple available variants
      const optionName = product.options?.[0] || 'option';

      // Native select options for mobile
      const nativeOptionsHtml = availableVariants.map(variant => {
        return `<option value="${variant.id}">${this.escapeHtml(variant.title)}</option>`;
      }).join('');

      // Button dropdown options for desktop
      const variantOptionsHtml = availableVariants.map(variant => {
        return `<button type="button" class="cart-drawer__upsell-variant-option" data-variant-id="${variant.id}" data-action="add-to-cart">${this.escapeHtml(variant.title)}</button>`;
      }).join('');

      actionHtml = `
        <div class="cart-drawer__upsell-native-select-wrapper">
          <select class="cart-drawer__upsell-native-select" data-action="native-variant-select" aria-label="${this.getTranslation('choose')} ${optionName.toLowerCase()}">
            <option value="" disabled selected>${this.getTranslation('choose')} ${optionName.toLowerCase()}</option>
            ${nativeOptionsHtml}
          </select>
          <span class="cart-drawer__upsell-native-select-icon">${this.getChevronIcon()}</span>
        </div>
        <div class="cart-drawer__upsell-variant-wrapper">
          <button type="button" class="cart-drawer__upsell-add-button cart-drawer__upsell-add-button--variant" data-action="show-variants">
            <span class="cart-drawer__upsell-add-button-content">
              <span class="cart-drawer__upsell-add-text">${this.getTranslation('choose')} ${optionName.toLowerCase()}</span>
              <span class="svg-wrapper cart-drawer__upsell-chevron">${this.getChevronIcon()}</span>
            </span>
            <span class="cart-drawer__upsell-add-button-added" aria-hidden="true">
              <span class="svg-wrapper">${this.getCheckmarkIcon()}</span>
              <span>${this.getTranslation('added')}</span>
            </span>
          </button>
          <div class="cart-drawer__upsell-variant-dropdown" hidden>
            ${variantOptionsHtml}
          </div>
        </div>
      `;
    } else {
      // Single variant or no available variants - show direct add button
      actionHtml = firstAvailableVariant?.available
        ? `<button type="button" class="cart-drawer__upsell-add-button" data-variant-id="${firstAvailableVariant.id}" data-action="add-to-cart">
            <span class="cart-drawer__upsell-add-button-content">
              <span class="svg-wrapper cart-drawer__upsell-add-icon">${this.getAddToCartIcon()}</span>
              <span class="cart-drawer__upsell-add-text">${this.getTranslation('add')}</span>
            </span>
            <span class="cart-drawer__upsell-add-button-added" aria-hidden="true">
              <span class="svg-wrapper">${this.getCheckmarkIcon()}</span>
              <span>${this.getTranslation('added')}</span>
            </span>
          </button>`
        : `<button type="button" class="cart-drawer__upsell-add-button" disabled>
            <span class="cart-drawer__upsell-add-button-content">
              <span class="cart-drawer__upsell-add-text">${this.getTranslation('sold_out')}</span>
            </span>
          </button>`;
    }

    return `
      <div class="cart-drawer__upsell-product" data-product-id="${product.id}" data-product-handle="${product.handle}">
        <a href="${product.url}" class="cart-drawer__upsell-product-link">
          ${imageUrl
            ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" width="200" height="200" loading="lazy" class="cart-drawer__upsell-product-image">`
            : `<div class="cart-drawer__upsell-product-image cart-drawer__upsell-product-image--placeholder"></div>`
          }
        </a>
        <div class="cart-drawer__upsell-product-info">
          <h4 class="cart-drawer__upsell-product-title">
            <a href="${product.url}">${this.escapeHtml(product.title)}</a>
          </h4>
          ${vendorHtml}
          <div class="cart-drawer__upsell-product-price">${priceHtml}</div>
          ${actionHtml}
        </div>
      </div>
    `;
  }

  handleVariantSelect(select) {
    const productCard = select.closest('.cart-drawer__upsell-product');
    const addButton = productCard.querySelector('[data-action="add-to-cart"]');
    const selectedOption = select.options[select.selectedIndex];

    if (select.value && selectedOption.dataset.available === 'true') {
      addButton.disabled = false;
      addButton.dataset.variantId = select.value;

      // Update price display if variant has different price
      const priceContainer = productCard.querySelector('.cart-drawer__upsell-product-price');
      if (priceContainer && selectedOption.dataset.price) {
        const price = parseInt(selectedOption.dataset.price);
        const comparePrice = selectedOption.dataset.comparePrice ? parseInt(selectedOption.dataset.comparePrice) : null;

        if (comparePrice && comparePrice > price) {
          priceContainer.innerHTML = `
            <span class="cart-drawer__upsell-product-price--sale">${this.formatMoney(price)}</span>
            <span class="cart-drawer__upsell-product-price--compare">${this.formatMoney(comparePrice)}</span>
          `;
        } else {
          priceContainer.innerHTML = this.formatMoney(price);
        }
      }
    } else {
      addButton.disabled = true;
      delete addButton.dataset.variantId;
    }
  }

  async handleAddToCart(button) {
    // Button is passed directly from delegated click handler
    const variantId = button.dataset.variantId;

    if (!variantId) {
      console.warn('Cart upsell: No variant ID found on button', button);
      return;
    }

    // Find the main add button (could be the button itself or parent wrapper's button)
    const wrapper = button.closest('.cart-drawer__upsell-variant-wrapper');
    const mainButton = wrapper
      ? wrapper.querySelector('.cart-drawer__upsell-add-button--variant')
      : button;

    // Close dropdown if open
    if (wrapper) {
      this.closeAllDropdowns();
    }

    // Prevent double-clicks
    if (mainButton.disabled) return;

    // Set loading state on main button
    mainButton.disabled = true;
    mainButton.classList.add('cart-drawer__upsell-add-button--loading');

    try {
      // Get section IDs from cart-items-components for section rendering
      const sectionIds = this.getCartSectionIds();

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const result = await response.json();

      // Dispatch cart:update event with sections data for theme's cart-items-component
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: 'cart-drawer-upsell',
          data: {
            itemCount: result.item_count,
            source: 'cart-drawer-upsell',
            variantId: variantId,
            sections: result.sections || {}
          }
        }
      }));

      // Show success state with animation on main button
      mainButton.classList.remove('cart-drawer__upsell-add-button--loading');
      mainButton.classList.add('cart-drawer__upsell-add-button--added');

      setTimeout(() => {
        // Remove the product card from upsell after successful add
        const productCard = mainButton.closest('.cart-drawer__upsell-product');
        if (productCard) {
          productCard.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          productCard.style.opacity = '0';
          productCard.style.transform = 'scale(0.9)';
          setTimeout(() => {
            productCard.remove();
            // Hide section if no products left
            if (this.querySelectorAll('.cart-drawer__upsell-product').length === 0) {
              this.hidden = true;
            }
          }, 300);
        }
      }, 1000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      mainButton.disabled = false;
      mainButton.classList.remove('cart-drawer__upsell-add-button--loading');
      mainButton.classList.remove('cart-drawer__upsell-add-button--added');
    }
  }

  /**
   * Get section IDs from all cart-items-components on the page
   */
  getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    // Add cart-icon-bubble for updating the cart count
    sectionIds.add('cart-icon-bubble');
    return Array.from(sectionIds);
  }

  handleCartChange(event) {
    // Skip if we triggered this event ourselves
    if (event.detail?.source === 'cart-drawer-upsell') return;

    // Reload the page section to get updated cart data and re-render upsell
    // This ensures cart product IDs are up to date
    this.updateUpsellSection();
  }

  async updateUpsellSection() {
    try {
      // Fetch fresh cart data
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();

      if (cart.item_count === 0) {
        this.showNoProducts();
        return;
      }

      // Update cart product IDs
      this.cartProductIds = cart.items.map(item => item.product_id);
      this.cartVariantIds = cart.items.map(item => item.variant_id);
      this.productId = String(cart.items[0]?.product_id || '');

      // Update data attributes
      this.dataset.cartProductIds = this.cartProductIds.join(',');
      this.dataset.cartVariantIds = this.cartVariantIds.join(',');
      this.dataset.productId = this.productId;

      // Remove any products that are now in cart
      this.querySelectorAll('.cart-drawer__upsell-product').forEach(card => {
        const productId = parseInt(card.dataset.productId);
        if (this.cartProductIds.includes(productId)) {
          card.remove();
        }
      });

      // If no products left and using complementary, reload
      if (this.querySelectorAll('.cart-drawer__upsell-product').length === 0) {
        if (!this.useCollection) {
          await this.loadComplementaryProducts();
        } else {
          this.showNoProducts();
        }
      }

      // Show component if we have products
      if (this.querySelectorAll('.cart-drawer__upsell-product').length > 0) {
        this.hidden = false;
      }
    } catch (error) {
      console.error('Error updating upsell section:', error);
    }
  }

  // Utility functions
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }

    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';

    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|[0-9]+x[0-9]*)\./, `_${size}.`)
        .replace(/\.jpg|\.jpeg|\.png|\.gif/, match => `_${size}${match}`);
    }

    return url;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getTranslation(key) {
    const translations = {
      add: window.theme?.strings?.add || 'Add',
      added: window.theme?.strings?.added || 'Added',
      sold_out: window.theme?.strings?.soldOut || 'Sold out',
      choose: window.theme?.strings?.choose || 'Choose'
    };
    return translations[key] || key;
  }

  getAddToCartIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>`;
  }

  getCheckmarkIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>`;
  }

  getChevronIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>`;
  }
}

// Register the custom element
if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
